import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Tabs, TabPanel } from 'anf-core-react';
import { useMutation } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import useSwitchesFromContext from '../../../../hooks/useSwitchesFromContext';
import DELIVERY_OPTION_TAB_UPDATE_MUTATION from '../../../../gql/deliveryOptionTab.gql';
import CheckoutPageContext from '../../../../context/CheckoutPageContext';
import useLog from '../../../useLog/useLog';
import $window from '../../../../tools/window';

export default function DeliveryTabs() {
  const logger = useLog('checkout.changeDeliveryType');
  const DELIVERY = 'delivery';
  const PICKUP = 'pickup';
  const [deliveryMethod, setDeliveryMethod] = useState(DELIVERY);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const previousHomeDelivery = useRef();
  const previousPickup = useRef();
  const { getSwitchValue } = useSwitchesFromContext();
  const isDeliveryTabOptionEnabled = getSwitchValue('fulDeliveryOptionsTabs');
  const hasSteppedCheckout = getSwitchValue('chkHasSteppedCheckout');

  const {
    isUpdating, checkoutPageState, setCheckoutPageState, setIsUpdating,
  } = useContext(CheckoutPageContext);
  const { reset, getValues } = useFormContext();

  useEffect(() => {
    if (checkoutPageState?.shipMethod) {
      setDeliveryMethod(checkoutPageState?.shipMethod?.isHomeDelivery ? DELIVERY : PICKUP);
      setIsTabVisible(checkoutPageState?.shipMethod?.isTabVisible);
    }
  }, [checkoutPageState?.shipMethod]);
  const [changeDeliveryTypeMutation] = useMutation(DELIVERY_OPTION_TAB_UPDATE_MUTATION, {
    onCompleted: (response) => {
      logger.debug('DELIVERY_OPTION_TAB_UPDATE_MUTATION RESULT', response);
      if (response.changeDeliveryType) {
        const { orderContact: prevOrderContact } = checkoutPageState;
        const { orderContact } = response.changeDeliveryType;
        const email = getValues(`addresses.${prevOrderContact.addressType}.email`);
        const phone = getValues(`addresses.${prevOrderContact.addressType}.phone`);
        setCheckoutPageState((currentState) => ({
          ...currentState, ...response.changeDeliveryType,
        }));
        reset((prev) => ({
          ...prev,
          addresses: {
            ...prev?.addresses,
            [orderContact.addressType]: {
              ...prev?.addresses?.[orderContact.addressType],
              email,
              phone,
            },
          },
        }));
      }
      $window.dispatchEvent(new CustomEvent('mfe:changeDeliveryType:update:done', { detail: response.changeDeliveryType.cartInfo }));

      setIsUpdating(false);
    },
    onError: (err) => {
      setIsUpdating(false);
      logger.error(`ERR: DELIVERY_OPTION_TAB_UPDATE_MUTATION: ${JSON.stringify(err)}`);
    },
  });

  const { textFor = {} } = checkoutPageState || {};
  const homeDeliveryLabel = hasSteppedCheckout
    ? textFor?.delivery?.value : textFor?.homeDelivery?.value;
  const pickupMyOrderLAbel = hasSteppedCheckout
    ? textFor?.pickup?.value : textFor?.pickupMyOrder?.value;

  if (!isDeliveryTabOptionEnabled || !textFor || !isTabVisible) {
    return null;
  }
  const onChange = (id) => {
    if (isUpdating) return;
    const { shippingAddress, shipMethod, shipOrPickup } = checkoutPageState;
    const shipMethodObj = {
      shipModeId: shipMethod.shipModeId,
      shipModeType: shipMethod.shipModeType,
      shippingAddress: {
        country: shippingAddress.country,
        addressId: shippingAddress.addressId,
        addressType: shippingAddress.addressType,
        address1: shippingAddress.address1,
        address2: shippingAddress.address2,
        city: shippingAddress.city,
        postalCode: shippingAddress.postalCode,
        state: shippingAddress.state,
        province: shippingAddress.province,
        displayType: shippingAddress.displayType,
        district: shippingAddress.district,
        firstName: shippingAddress.firstName,
        isAPOFPO: shippingAddress.isAPOFPO,
        isBillingAllowed: shippingAddress.isBillingAllowed,
        isDefaultBilling: shippingAddress.isDefaultBilling,
        isDefaultShipping: shippingAddress.isDefaultShipping,
        isShippingAllowed: shippingAddress.isShippingAllowed,
        lastName: shippingAddress.lastName,
        legendAddress: shippingAddress.legendAddress,
        legendName: shippingAddress.legendName,
        orgName: shippingAddress.orgName,
        pickUpStoreId: shippingAddress.pickUpStoreId,
        poBox: shippingAddress.poBox,
        sameDayStoreId: shippingAddress.sameDayStoreId,
        subType: shippingAddress.subType,
        title: shippingAddress.title,
        collectionPointID: shippingAddress.collectionPointID,
      },
    };

    if (deliveryMethod === DELIVERY && (shippingAddress.addressType === 'SB' || shippingAddress.addressType === 'S')) {
      previousHomeDelivery.current = shipMethodObj;
    } else if (deliveryMethod === PICKUP && (shippingAddress.addressType === 'P' || shippingAddress.addressType === 'CP')) {
      previousPickup.current = shipMethodObj;
    }
    const variables = {
      deliveryType: id,
      previousHomeDelivery: previousHomeDelivery.current,
      previousPickup: previousPickup.current,
      activeShippingAddress: {
        country: shippingAddress.country,
        addressType: shippingAddress.addressType,
      },
      availableShippingMethods: shipOrPickup?.availableShippingMethods?.map((speed) => ({
        shipModeId: speed.shipModeId,
        shipModeType: speed.shipModeType,
        externalId: speed.externalId,
      })) || [],
    };
    setIsUpdating(true);

    setDeliveryMethod(id);
    changeDeliveryTypeMutation({
      variables,
    });
  };
  return (
    <Tabs
      controls={{
        onKeyDown: () => {},
        tabs: [
          {
            ariaControls: `${DELIVERY}-tabpanel`,
            id: DELIVERY,
            isSelected: deliveryMethod === DELIVERY,
            label: homeDeliveryLabel,
            onClick: () => onChange(DELIVERY),
            subLabel: undefined,
          },
          {
            ariaControls: `${PICKUP}-tabpanel`,
            id: PICKUP,
            isSelected: deliveryMethod === PICKUP,
            label: pickupMyOrderLAbel,
            onClick: () => onChange(PICKUP),
            subLabel: undefined,
          },
        ],
      }}
      labelText="Delivery Option"
      orientation="horizontal"
    >
      <TabPanel
        ariaLabelledBy={homeDeliveryLabel}
        id={`${DELIVERY}-tabpanel`}
        isVisible={false}
      >
        {homeDeliveryLabel}
      </TabPanel>
      <TabPanel
        ariaLabelledBy={pickupMyOrderLAbel}
        id={`${PICKUP}-tabpanel`}
        isVisible={false}
      >
        {pickupMyOrderLAbel}
      </TabPanel>
    </Tabs>
  );
}
